/**
 * GuestGateCard — what stands where the composer was once a guest is stopped
 * (hard limit, token cap, today's budget, guest mode off);
 * the conversation above stays readable and its product links keep working.
 * GuestComposerNotice — the small print under a guest's composer.
 *
 * DRAFTED STATE (GUARDRAILS rule 10): no prototype counterpart. Same width and
 * padding as the composer it replaces, a slip-like surface with a drawn rule,
 * a lock glyph on the icon ladder, and the shared sign-in actions in a row.
 * It enters with the house fade-and-rise (`bubbleMotion`, transform/opacity
 * only, nothing under reduced motion) and is announced through a polite live
 * region.
 *
 * Fix round (Important): when this card REPLACES a focused MessageInput
 * (the allowance frame's re-render unmounts it — the textarea or the just-
 * clicked send button may hold focus at that instant), the browser drops
 * focus to `<body>` with nothing to pick it back up. `focusOnMount` (set by
 * the caller only for a mid-session transition, never a cold load) moves
 * focus onto this card's own section instead, and does so BEFORE the sheet
 * that may open alongside it (GuestGateSheet, a later sibling in ChatRoute's
 * tree — its effects run after this component's) ever records an "opener"
 * for its own close-time focus restore. That ordering is what makes
 * BottomSheet restore focus to the card, never to `<body>`, when the sheet
 * closes.
 */
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { GuestAllowance, GuestStopCause } from "@/features/chat/guest/allowance";
import { GuestSignInActions, useGateCopy } from "@/features/chat/guest/GuestGateSheet";
import { iconProps } from "@/lib/icons";
import { bubbleMotion } from "@/lib/motion";
import { useGardenMotion } from "@/lib/use-garden-motion";

export interface GuestGateCardProps {
  cause: GuestStopCause;
  allowance: GuestAllowance | null;
  returnTo: string;
  /**
   * Take focus on mount — set by the caller (ChatRoute) only when the
   * composer was showing on the PREVIOUS render (a transition during the
   * session), never on a cold load where nothing had focus to begin with.
   * Must not depend on whether a sheet opens alongside the card: the server
   * may send `exhausted` with `show_prompt: false`.
   */
  focusOnMount?: boolean;
}

export function GuestGateCard({
  cause,
  allowance,
  returnTo,
  focusOnMount = false,
}: GuestGateCardProps): ReactElement {
  const copy = useGateCopy({ reason: "limit", cause, hard: true }, allowance);
  const { reduced } = useGardenMotion();
  const titleId = useId();
  const title = copy?.title ?? "";
  const sectionRef = useRef<HTMLElement | null>(null);
  // `focusOnMount` is the caller's decision at the moment this card appeared
  // (see the file header). Keyed on that decision alone, so an unrelated
  // re-render never re-fires it: ChatRoute only ever turns it true on the
  // render that swaps the composer for the card, and false on the next one —
  // never back to true while the card stays mounted.
  useEffect(() => {
    if (focusOnMount) sectionRef.current?.focus({ preventScroll: true });
  }, [focusOnMount]);
  // A live region announces CHANGES; set the text after mount so it is
  // heard. Fix round 1: deferred one macrotask (not just one React commit) —
  // several screen readers need the live region to exist, stable, for a
  // full tick before a mutation inside it registers as a "change" to
  // announce; populating it in the very same frame the region itself
  // mounts can get coalesced into the initial (silent) paint.
  const [announcement, setAnnouncement] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setAnnouncement(title), 0);
    return () => clearTimeout(timer);
  }, [title]);

  return (
    <div
      className="border-t border-[var(--g-rule)] bg-[var(--chat-ground)] px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3"
      data-testid="chat-guest-gate-card"
      data-cause={cause}
    >
      <motion.section
        ref={sectionRef}
        role="region"
        aria-labelledby={titleId}
        // -1: programmatically focusable (the mount effect above) without
        // joining the Tab order — the card is a landing spot, not a widget.
        // The ring's offset gap takes the colour AROUND the card — the
        // strip's ground (`--chat-ground`), not the card's
        // own surface (final web fix round, item 12).
        tabIndex={-1}
        className="mx-auto flex w-full max-w-[760px] flex-col gap-3 rounded-[var(--g-radius-lg)] border border-[var(--g-rule)] bg-[var(--g-surface)] p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--chat-ground)]"
        {...bubbleMotion(!reduced)}
      >
        <div className="flex items-start gap-3">
          <span aria-hidden="true" className="mt-0.5 shrink-0 text-primary">
            <Lock {...iconProps("md")} />
          </span>
          <div className="min-w-0">
            <h2 id={titleId} className="text-[16px] font-semibold leading-snug text-foreground">
              {title}
            </h2>
            <p className="mt-1 text-[14.5px] leading-[1.5] text-[var(--g-ink-2)]">{copy?.body}</p>
          </div>
        </div>
        <GuestSignInActions returnTo={returnTo} layout="row" />
      </motion.section>
      <p aria-live="polite" className="sr-only" data-testid="chat-guest-gate-announce">
        {announcement}
      </p>
    </div>
  );
}

export interface GuestComposerNoticeProps {
  /** `retention_days` from the guest config (RU plural by days). */
  retentionDays: number;
  /** The "N left" hint; null unless the admin switched it on and the allowance says "warn". */
  remaining: number | null;
}

/**
 * Terms + retention under a guest's composer (spec "Land"). No links yet: the
 * Terms/Privacy pages do not exist (STORY-007-010); counsel signs this copy
 * off before PROD (spec open item).
 */
export function GuestComposerNotice({ retentionDays, remaining }: GuestComposerNoticeProps): ReactElement {
  const { t } = useTranslation("chat");
  return (
    <div
      className="mt-2 flex flex-col gap-0.5 px-1 text-[13px] leading-[1.45] text-[var(--g-ink-2)]"
      data-testid="chat-guest-notice"
    >
      {remaining !== null ? (
        <p className="font-semibold text-foreground" data-testid="chat-guest-remaining">
          {t("guest.remaining", { count: remaining })}
        </p>
      ) : null}
      <p>{t("guest.notice", { count: retentionDays })}</p>
    </div>
  );
}

export default GuestGateCard;
